/**
 * Wires the abstract side-effect action names declared by `playbackMachine`
 * to real subsystem calls via `IPlaybackSceneFacade`.
 *
 * This is the **only** place in the codebase where the machine's actions
 * become real imperative work. Tests can override the facade for unit
 * tests; the scene's facade implementation is the production path.
 *
 * Usage:
 * ```ts
 * const actions = buildSceneActions(facade);
 * const actor = createActor(
 *   playbackMachine.provide({ actions }),
 *   { input: { tempo, isLoopingActive, ... } },
 * ).start();
 * ```
 */

import { NoteType } from '@museflow-ai/opensheetmusicdisplay';

import type { IPlaybackSceneFacade } from './IPlaybackSceneFacade';
import type { PlaybackContext, PlaybackEvent } from './playbackMachine.types';
import { SIDE_EFFECT_ACTION_NAMES, type SideEffectActionName } from './playbackMachine';

/** An XState v5 action callback. Receives a snapshot of context + event. */
type ActionCallback = (params: { context: PlaybackContext; event: PlaybackEvent }) => void;

export function buildSceneActions(
  facade: IPlaybackSceneFacade,
): Record<SideEffectActionName, ActionCallback> {
  return {
    armAuxTK: ({ context }) => {
      facade.armAuxTK(context.tempo, NoteType._32nd);
    },

    pauseAuxTK: () => {
      facade.pauseAuxTK();
    },

    pauseMainTK: () => {
      facade.pauseMainTK();
    },

    unpauseMainTKAt: ({ context }) => {
      // Resume target depends on transition origin:
      //   - From loopCountInWait → playing: bracketStart
      //   - From paused → playing: lastPausedAtTimestamp (or bracketStart fallback)
      const targetTimestamp = context.lastPausedAtTimestamp ?? context.bracketStartTimestamp ?? 0;
      facade.unpauseMainTKAt(targetTimestamp, context.tempo);
    },

    pauseScheduler2: () => {
      facade.pauseScheduler2();
    },

    unpauseScheduler2At: ({ context }) => {
      const targetTimestamp = context.lastPausedAtTimestamp ?? context.bracketStartTimestamp ?? 0;
      facade.unpauseScheduler2At(targetTimestamp, context.tempo);
    },

    clearScheduler2: () => {
      facade.clearScheduler2();
    },

    setScheduler2LoopBounds: ({ context, event }) => {
      // Prefer event payload (USER_BRACKET_CHANGE carries fresh bounds);
      // fall back to context if invoked from a different event source.
      if (event.type === 'USER_BRACKET_CHANGE') {
        facade.setScheduler2LoopBounds(event.bracketStartTimestamp, event.bracketEndTimestamp);
        return;
      }
      if (context.bracketStartTimestamp !== null && context.bracketEndTimestamp !== null) {
        facade.setScheduler2LoopBounds(context.bracketStartTimestamp, context.bracketEndTimestamp);
      }
    },

    enableCursorBlink: ({ context }) => {
      const beatDurationMs = (60 / context.tempo) * 1000;
      facade.enableCursorBlink(beatDurationMs);
    },

    unpauseCursor: () => {
      facade.unpauseCursor();
    },

    pauseCursor: () => {
      facade.pauseCursor();
    },

    positionCursorAtBracketStart: ({ context }) => {
      if (context.bracketStartTimestamp !== null) {
        facade.positionCursorAtBracketStart(context.bracketStartTimestamp);
      }
    },

    positionCursorAtLastCompletedMeasure: () => {
      facade.positionCursorAtLastCompletedMeasure();
    },

    resetLoopVisuals: ({ context }) => {
      if (context.bracketStartTimestamp !== null && context.bracketEndTimestamp !== null) {
        facade.resetLoopVisuals(context.bracketStartTimestamp, context.bracketEndTimestamp);
      }
    },

    disableErrorRecognition: () => {
      facade.disableErrorRecognition();
    },

    restoreErrorRecognition: () => {
      facade.restoreErrorRecognition();
    },
  };
}

// Compile-time guarantee: this builder covers every declared action name.
// If a new action is added to SIDE_EFFECT_ACTION_NAMES without a matching
// case here, the assignment below fails type-check.
type _CoversAll = Record<(typeof SIDE_EFFECT_ACTION_NAMES)[number], ActionCallback>;
const _typeCheck: _CoversAll = buildSceneActions({} as IPlaybackSceneFacade);
void _typeCheck;
